<script setup lang="ts">
import AssistantMessageChart from '@/components/assistant/chat/message/AssistantMessageChart.vue'
import AssistantMessageContent from '@/components/assistant/chat/message/AssistantMessageContent.vue'
import AssistantMessageFeedbackModalContent from '@/components/assistant/chat/message/AssistantMessageFeedbackModalContent.vue'
import AssistantMessageSuggestions from '@/components/assistant/chat/message/AssistantMessageSuggestions.vue'
import AssistantMessageTable from '@/components/assistant/chat/message/AssistantMessageTable.vue'
import AssistantMessageToggle from '@/components/assistant/chat/message/AssistantMessageToggle.vue'
import MessageLayout from '@/components/assistant/chat/message/MessageLayout.vue'
import type {AssistantMessage} from '@/client/mentor/MessagesClient'
import {MessageFeedbackRating} from '@/client/mentor/MessagesClient'
import {assertNonNil} from '@/utils/assert'
import {assistantChatMessageContextKey} from '@/components/assistant/chat/messageContext'
import {computed, inject, ref} from 'vue'
import {useAssistantMessageExport} from '@/components/assistant/composables/useAssistantMessageExport'
import {useAssistantMessageFeedback} from '@/components/assistant/composables/useAssistantMessageFeedback'
import {useAssistantMessagePrint} from '@/components/assistant/composables/useAssistantMessagePrint'

const emit = defineEmits<{
  selectSuggestion: [suggestion: string]
}>()

const props = defineProps<{
  disabled?: boolean
  isLastMessage?: boolean
  message: AssistantMessage
}>()

const messageContext = inject(assistantChatMessageContextKey)
const isMobileVersion = computed(() => messageContext?.isMobileVersion.value ?? false)

const {exportChartToPng} = useAssistantMessageExport()
const {feedbackModalId, feedbackType, feedbackMessage, feedbackMessageError, openFeedbackModal, getFeedbackButtonClass} =
  useAssistantMessageFeedback(assertNonNil(messageContext?.companyId?.value), props.message)
const {printMessage} = useAssistantMessagePrint()

const containerRef = ref<HTMLElement | null>(null)
const chartContainerRef = ref<InstanceType<typeof AssistantMessageChart> | null>(null)

function onPrintMessage() {
  if (containerRef.value) {
    printMessage(containerRef.value)
  }
}

function onDownloadChartPng() {
  const svg = chartContainerRef.value?.$el?.querySelector('svg') as SVGSVGElement | null
  if (svg) {
    exportChartToPng(svg, 'chart.png')
  }
}
</script>

<template>
  <div ref="containerRef" class="flex flex-row gap-1 print:w-full" :class="{'w-full': isMobileVersion}">
    <!-- AI Badge -->
    <div class="w-5 h-5 rounded-md flex shrink-0 items-center justify-center bg-new-primary-hover print:hidden">
      <span class="text-xs text-white">{{ $t('assistant.icon.label') }}</span>
    </div>

    <!-- Message with optional suggestions -->
    <div class="flex min-w-0 flex-col gap-2 print:w-full">
      <MessageLayout class="min-w-0 border-new-sm border-new-neutral-100 bg-new-neutral-0 print:border-none" :message="message">
        <div class="flex min-w-0 flex-col gap-2 text-new-neutral-700">
          <AssistantMessageContent :content="message.content.answer" />

          <AssistantMessageToggle
            v-if="message.content.tableData"
            :is-last-message="isLastMessage"
            :label="$t('assistant.chat.message.toggle.table')"
            :message-id="message.id"
            toggle-key="table"
          >
            <AssistantMessageTable :table-data="message.content.tableData" />
          </AssistantMessageToggle>

          <AssistantMessageToggle
            v-if="message.content.chartData"
            :is-last-message="isLastMessage"
            :label="$t('assistant.chat.message.toggle.chart')"
            :message-id="message.id"
            toggle-key="chart"
          >
            <AssistantMessageChart ref="chartContainerRef" :chart-data="message.content.chartData" />

            <template #download-menu>
              <button
                class="flex flex-row items-center gap-2 px-2 py-1 text-base rounded-md"
                type="button"
                @click="onDownloadChartPng"
              >
                <i class="fa-solid fa-file-image"></i>
                <span>{{ $t('assistant.chat.message.download.chart.png') }}</span>
              </button>
            </template>
          </AssistantMessageToggle>

          <AssistantMessageToggle
            v-if="message.content.sources && message.content.sources.length > 0"
            :is-last-message="isLastMessage"
            :label="$t('assistant.chat.message.toggle.sources')"
            :message-id="message.id"
            toggle-key="sources"
          >
            <a
              v-for="source in message.content.sources"
              :key="source.url"
              class="flex flex-row items-center gap-2 break-all text-sm"
              :href="source.url"
              rel="noopener noreferrer"
              target="_blank"
            >
              <i class="fa-solid fa-link text-xs"></i>
              <span>{{ source.title || source.url }}</span>
            </a>
          </AssistantMessageToggle>

          <div class="flex items-center justify-end gap-1.5 print:hidden">
            <button
              v-tt="$t('assistant.chat.message.exportMessage')"
              class="group inline-flex items-center justify-center text-base transition-colors duration-150 text-new-neutral-300 hover:text-new-neutral-500"
              type="button"
              @click="onPrintMessage"
            >
              <i class="fa-solid fa-arrow-right-to-bracket rotate-90 transition-transform duration-150 group-hover:scale-110"></i>
            </button>
            <button
              v-tt="$t('assistant.chat.message.feedback.thumbsUp')"
              class="group inline-flex items-center justify-center text-base transition-colors duration-150"
              :class="getFeedbackButtonClass(MessageFeedbackRating.THUMBS_UP)"
              type="button"
              @click="openFeedbackModal(MessageFeedbackRating.THUMBS_UP)"
            >
              <i class="fa-solid fa-thumbs-up transition-transform duration-150 group-hover:scale-110"></i>
            </button>
            <button
              v-tt="$t('assistant.chat.message.feedback.thumbsDown')"
              class="group inline-flex items-center justify-center text-base transition-colors duration-150"
              :class="getFeedbackButtonClass(MessageFeedbackRating.THUMBS_DOWN)"
              type="button"
              @click="openFeedbackModal(MessageFeedbackRating.THUMBS_DOWN)"
            >
              <i class="fa-solid fa-thumbs-down transition-transform duration-150 group-hover:scale-110"></i>
            </button>
          </div>
        </div>
      </MessageLayout>

      <AssistantMessageSuggestions
        v-if="isLastMessage && message.content.suggestions && message.content.suggestions.length > 0"
        :disabled="disabled"
        :is-mobile-version="isMobileVersion"
        :suggestions="message.content.suggestions"
        @select="emit('selectSuggestion', $event)"
      />
    </div>
  </div>

  <AssistantMessageFeedbackModalContent
    v-if="feedbackModalId"
    :id="feedbackModalId"
    v-model="feedbackMessage"
    :error="feedbackMessageError"
    :feedback-type="feedbackType"
  />
</template>
